import { mat4, vec3, vec4 } from "gl-matrix";
import { Camera } from "./Camera";
import Common from "./CommonRender";
import Light from "./Lights/Light";
import { PointLight } from "./Lights/PointLight";
import { RenderObject } from "./Objects/RenderObject";
import { ObjectType } from "./Objects/types";
import { ProgramInfo } from "./ProgramInfo";
import { Picker } from "./SceneManager";
import { ShaderManager, ShaderType } from "./ShaderManager";

function resizeCanvasToDisplaySize(canvas: HTMLCanvasElement): { width: number, height: number } {
  const dpr = window.devicePixelRatio || 1;
  const width = Math.floor(canvas.clientWidth * dpr);
  const height = Math.floor(canvas.clientHeight * dpr);

  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }
  return { width, height }; // resized
}

export type RendererArgs = {
  canvas: HTMLCanvasElement;
  near?: number;
  far?: number;
  fov?: number;
  backgroundColour?: [number, number, number];
}

export class Renderer {
  private _projectionMatrix: mat4;
  private _near: number;
  private _far: number;
  private _fov: number;
  private _canvas: HTMLCanvasElement | undefined;
  private _viewPortWidth = 0;
  private _viewPortHeight = 0;
  private _backgroundColour : [number, number, number] = [0.0, 0.0, 0.0];

  constructor(args: RendererArgs) {
    this._far = args.far ?? 10000;
    this._near = args.near ?? 0.1;
    this._fov = args.fov ?? 60;
    this._canvas = args.canvas;

    this._projectionMatrix = this.initProjection();
  }

  handleScreenResize = () => {
    if (!this._canvas) return;
    this._viewPortHeight = this._canvas.clientHeight;
    this._viewPortWidth = this._canvas.clientWidth;
  }

  getCanvas(): HTMLCanvasElement | undefined {
    return this._canvas;
  }

  setBackgroundColour(colour: vec3) {
    this._backgroundColour = [colour[0], colour[1], colour[2]];
  }

  getBackgroundColour(): [number, number, number] {
    return this._backgroundColour;
  }

  initProjection() {
    // try and get the parent of the canvas
    if (!this._canvas)
      throw new Error("missing canvas");
    const projectionMatrix = mat4.create();
    // set the cameras info from these details
    let fovy = (this._fov * Math.PI) / 180.0; // Vertical field of view in radians

    this._viewPortHeight = this._canvas.clientHeight;
    this._viewPortWidth = this._canvas.clientWidth;

    // add event listener that will update the projection matrix 
    window.addEventListener('resize', this.handleScreenResize)

    let aspect = this._canvas.clientWidth / this._canvas.clientHeight; // Aspect ratio of the canvas
    mat4.perspective(projectionMatrix, fovy, aspect, this._near, this._far);
    return projectionMatrix;
  }

  applyTransformations(gl: WebGL2RenderingContext, object: RenderObject, programInfo?: ProgramInfo) {
    const modelMatrix = object.getModelMatrix();
    let programInfoToUse = programInfo ? programInfo : object.programInfo;
    if (!programInfoToUse) {
      console.error("no valid program info for object");
      return;
    }

    gl.uniformMatrix4fv(
      programInfoToUse.uniformLocations.uModelMatrix,
      false,
      modelMatrix,
    );
    return modelMatrix;
  }

  applyView(gl: WebGL2RenderingContext, programInfo: ProgramInfo, camera: Camera) {
    const viewMatrix = camera.getViewMatrix();

    gl.uniformMatrix4fv(
      programInfo.uniformLocations.uViewMatrix,
      false,
      viewMatrix,
    );
    gl.uniform3fv(
      programInfo.uniformLocations.uCameraPosition,
      camera.position,
    );
  }

  applyMaterial(gl: WebGL2RenderingContext, object: RenderObject, programInfo: ProgramInfo) {
    if (!object.material) {
      return;
    }
    gl.uniform3fv(
      programInfo.uniformLocations.diffuseVal,
      object.material.diffuse,
    );
    gl.uniform3fv(
      programInfo.uniformLocations.ambientVal,
      object.material.ambient,
    );
    gl.uniform3fv(
      programInfo.uniformLocations.specularVal,
      object.material.specular,
    );
    gl.uniform1f(
      programInfo.uniformLocations.alpha,
      object.material.alpha,
    );
    gl.uniform1f(programInfo.uniformLocations.nVal, object.material.n);

    // handle textures
    // check for diffuse texture and apply it
    if (object.material.shaderType === ShaderType.BLINN_DIFFUSE_TEXTURE) {
      if (object.material.diffuseTexture?.texture) {
        gl.activeTexture(gl.TEXTURE0);
        gl.uniform1i(programInfo.uniformLocations.uTexture, 0);
        gl.bindTexture(gl.TEXTURE_2D, object.material.diffuseTexture.texture);
      }
    } else if (object.material.shaderType === ShaderType.BLINN_NORMAL_TEXTURE) {
      if (object.material.diffuseTexture?.texture && object.material.normalTexture?.texture) {
        gl.activeTexture(gl.TEXTURE0);
        gl.uniform1i(programInfo.uniformLocations.uTexture, 0);
        gl.bindTexture(gl.TEXTURE_2D, object.material.diffuseTexture.texture);

        gl.activeTexture(gl.TEXTURE0 + 1);
        gl.uniform1i(programInfo.uniformLocations.uTextureNorm, 1);
        gl.bindTexture(gl.TEXTURE_2D, object.material.normalTexture.texture);
      }
    }
  }

  applyNormalMatrix(gl: WebGL2RenderingContext, programInfo: ProgramInfo, modelMatrix: mat4) {
    var normalMatrix = mat4.create();
    mat4.invert(normalMatrix, modelMatrix);
    mat4.transpose(normalMatrix, normalMatrix);
    gl.uniformMatrix4fv(
      programInfo.uniformLocations.normalMatrix,
      false,
      normalMatrix,
    );
  }

  applyPointLights(gl: WebGL2RenderingContext, programInfo: ProgramInfo, pointLights: PointLight[]) {
    gl.uniform1i(
      programInfo.uniformLocations.numPointLights,
      pointLights.length,
    );
    pointLights.forEach((pL, index) => {
      let tempLightPosition = vec4.fromValues(
        pL.position[0],
        pL.position[1],
        pL.position[2],
        1.0,
      );

      // TODO - handle parent stuff
      // if (pL.parent) {
      //   let parent = this.getObject(pL.parent);
      //   if (parent.model && parent.model.modelMatrix) {
      //     vec4.transformMat4(
      //       tempLightPosition,
      //       tempLightPosition,
      //       parent.model.modelMatrix,
      //     );
      //   }
      // }

      let transformedPosition = vec3.fromValues(
        tempLightPosition[0],
        tempLightPosition[1],
        tempLightPosition[2],
      );
      gl.uniform3fv(
        gl.getUniformLocation(
          programInfo.program,
          "pointLights[" + index + "].position",
        ),
        transformedPosition,
      );
      gl.uniform3fv(
        gl.getUniformLocation(
          programInfo.program,
          "pointLights[" + index + "].color",
        ),
        pL.colour,
      );
      gl.uniform1f(
        gl.getUniformLocation(
          programInfo.program,
          "pointLights[" + index + "].strength",
        ),
        pL.strength,
      );
      gl.uniform1f(
        gl.getUniformLocation(
          programInfo.program,
          "pointLights[" + index + "].constant",
        ),
        pL.constant,
      );
      gl.uniform1f(
        gl.getUniformLocation(
          programInfo.program,
          "pointLights[" + index + "].linear",
        ),
        pL.linear,
      );
      gl.uniform1f(
        gl.getUniformLocation(
          programInfo.program,
          "pointLights[" + index + "].quadratic",
        ),
        pL.quadratic,
      );
    });
  }

  bindPickerBuffers(gl: WebGL2RenderingContext, picker?: Picker) {
    if (!picker || !picker.depthBuffer || !picker.frameBuffer || !picker.targetTexture || !this._canvas) {
      console.error("picker not initialized");
      return;
    }

    gl.clearColor(0.0, 0.0, 0.0, 1.0);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    gl.enable(gl.DEPTH_TEST); // Enable depth testing

    gl.bindTexture(gl.TEXTURE_2D, picker.targetTexture);
    // gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindRenderbuffer(gl.RENDERBUFFER, picker.depthBuffer);
    gl.bindFramebuffer(gl.FRAMEBUFFER, picker.frameBuffer);
    const attachmentPoint = gl.COLOR_ATTACHMENT0;
    const level = 0;
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      attachmentPoint,
      gl.TEXTURE_2D,
      picker.targetTexture,
      level,
    );
    gl.framebufferRenderbuffer(
      gl.FRAMEBUFFER,
      gl.DEPTH_ATTACHMENT,
      gl.RENDERBUFFER,
      picker.depthBuffer,
    );

    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA32F,
      this._canvas.width,
      this._canvas.height,
      0,
      gl.RGBA,
      gl.FLOAT,
      null,
    );

    gl.bindRenderbuffer(gl.RENDERBUFFER, picker.depthBuffer);
    gl.renderbufferStorage(
      gl.RENDERBUFFER,
      gl.DEPTH_COMPONENT32F,
      this._canvas.width,
      this._canvas.height,
    );
  }

  drawRegular(gl: WebGL2RenderingContext, objects: { [key: string]: RenderObject }, lights: { [key: string]: Light }, camera: Camera) {
    gl.clearColor(this._backgroundColour[0], this._backgroundColour[1], this._backgroundColour[2], 1.0);
    gl.enable(gl.DEPTH_TEST); // Enable depth testing
    gl.enable(gl.BLEND);
    gl.depthFunc(gl.LEQUAL); // Near things obscure far things
    gl.enable(gl.CULL_FACE);
    gl.cullFace(gl.BACK);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.enable(gl.STENCIL_TEST);
    gl.stencilFunc(gl.NOTEQUAL, 1, 0xff);
    gl.stencilOp(gl.KEEP, gl.KEEP, gl.REPLACE);
    gl.clearDepth(1.0);
    gl.stencilMask(0x00);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.STENCIL_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    if (this._canvas) {
      const { height, width } = resizeCanvasToDisplaySize(this._canvas);
      gl.viewport(0, 0, width, height);
    }

    // sort the objects
    let sortedObjects = Object.values(objects).sort((a, b) => {
      let aCentroidFour = a.getCentroid();
      vec4.transformMat4(aCentroidFour, aCentroidFour, a.getModelMatrix());

      let bCentroidFour = b.getCentroid();
      vec4.transformMat4(bCentroidFour, bCentroidFour, b.getModelMatrix());

      return vec3.distance(
        camera.position,
        vec3.fromValues(aCentroidFour[0], aCentroidFour[1], aCentroidFour[2]),
      ) >=
        vec3.distance(
          camera.position,
          vec3.fromValues(bCentroidFour[0], bCentroidFour[1], bCentroidFour[2]),
        )
        ? -1
        : 1;
    });

    // loop over objects and render them
    for (const object of sortedObjects) {
      if (!object.programInfo || !object.buffers)
        continue;

      gl.useProgram(object.programInfo?.program);

      let cullFaceCfg = true;
      if (object.type === ObjectType.PLANE) {
        cullFaceCfg = false;
      }

      if (object.material?.alpha && object.material?.alpha < 1.0) {
        gl.disable(gl.DEPTH_TEST);
      } else if (object.material?.alpha && object.material.alpha >= 1.0) {
        gl.enable(gl.DEPTH_TEST);
      }

      if (cullFaceCfg) {
        gl.enable(gl.CULL_FACE);
      } else {
        gl.disable(gl.CULL_FACE);
      }

      const modelMatrix = this.applyTransformations(gl, object);
      if (modelMatrix)
        this.applyNormalMatrix(gl, object.programInfo, modelMatrix);
      this.applyView(gl, object.programInfo, camera);
      this.applyMaterial(gl, object, object.programInfo);
      this.applyPointLights(gl, object.programInfo, Object.values(lights));

      gl.uniformMatrix4fv(
        object.programInfo.uniformLocations.uProjectionMatrix,
        false,
        this._projectionMatrix,
      );

      gl.bindVertexArray(object.buffers?.vao);

      if (object.type === ObjectType.MESH) {
        gl.drawArrays(
          gl.TRIANGLES,
          0,
          object.buffers.numVertices / 3,
        );
      } else {
        gl.drawElements(
          gl.TRIANGLES,
          object.buffers.numVertices,
          gl.UNSIGNED_SHORT,
          0,
        );
      }

    }
    gl.bindTexture(gl.TEXTURE_2D, null);
  }

  drawPicker(gl: WebGL2RenderingContext, shaderManager: ShaderManager, picker: Picker, objects: { [key: string]: RenderObject }, camera: Camera) {
    this.bindPickerBuffers(gl, picker);
    const shader = shaderManager.getShader("picker");

    if (!shader.program) {
      return;
    }
    const programInfo = Common.initShaderUniforms(
      gl,
      shader.program,
      shader.uniforms,
      shader.attribs,
    );

    if (this._canvas) {
      const { height, width } = resizeCanvasToDisplaySize(this._canvas);
      gl.viewport(0, 0, width, height);
    }

    let index = 0;
    for (const object of Object.values(objects)) {
      // const object = objects[key];
      if (!object.programInfo || !object.buffers)
        continue;

      gl.useProgram(programInfo.program);
      gl.uniformMatrix4fv(
        programInfo?.uniformLocations.uProjectionMatrix,
        false,
        this._projectionMatrix,
      );
      this.applyView(gl, programInfo, camera);
      this.applyTransformations(gl, object, programInfo);

      //create ID
      let uID: [number, number, number, number] = [
        (((index + 1) >> 0) & 0xff) / 255.0,
        (((index + 1) >> 8) & 0xff) / 255.0,
        (((index + 1) >> 16) & 0xff) / 255.0,
        1.0,
      ];
      object.id = uID;

      gl.uniform4fv(programInfo.uniformLocations.uID, uID);
      gl.bindVertexArray(object.buffers.vao);

      const offset = 0; // Number of elements to skip before starting
      //if its a mesh then we don't use an index buffer and use drawArrays instead of drawElements
      if (object.type === ObjectType.MESH) {
        gl.drawArrays(gl.TRIANGLES, offset, object.buffers.numVertices / 3);
      } else {
        gl.drawElements(
          gl.TRIANGLES,
          object.buffers.numVertices,
          gl.UNSIGNED_SHORT,
          offset,
        );
      }
      index++;
    };
  }

  drawStencil(gl: WebGL2RenderingContext, shaderManager: ShaderManager, camera: Camera, selectedObject?: RenderObject | Light) {
    if (
      !selectedObject ||
      selectedObject instanceof Light
    ) {
      return;
    }

    if (!selectedObject.buffers || !selectedObject.centroid) {
      console.error("selected object missing buffers or centroid calculation")
      return;
    }

    gl.cullFace(gl.FRONT);
    const shader = shaderManager.getShader("stencil");
    if (!shader.program) {
      console.error("no valid stencil shader program");
      return;
    }
    const programInfo = Common.initShaderUniforms(
      gl,
      shader.program,
      shader.uniforms,
      shader.attribs,
    );

    //gl.disable(gl.DEPTH_TEST); --- This line of code caused issues with the skybox and stencils.
    gl.stencilFunc(gl.NOTEQUAL, 1, 0xff);
    gl.stencilMask(0x00);
    let scale = 1.05;

    gl.useProgram(programInfo.program);
    gl.uniformMatrix4fv(
      programInfo?.uniformLocations.uProjectionMatrix,
      false,
      this._projectionMatrix,
    );

    this.applyView(gl, programInfo, camera);

    //custom logic for scaled outline
    let modelMatrix = mat4.create();
    let negCentroid = vec3.fromValues(0.0, 0.0, 0.0);
    vec3.negate(negCentroid, selectedObject.centroid);
    mat4.translate(modelMatrix, modelMatrix, selectedObject.model.position);
    mat4.translate(modelMatrix, modelMatrix, selectedObject.centroid);
    const rotationMatrix = mat4.create();
    mat4.fromQuat(rotationMatrix, selectedObject.model.rotation);
    mat4.mul(modelMatrix, modelMatrix, rotationMatrix);
    mat4.scale(modelMatrix, modelMatrix, selectedObject.model.scale);
    mat4.scale(modelMatrix, modelMatrix, vec3.fromValues(scale, scale, scale));
    mat4.translate(modelMatrix, modelMatrix, negCentroid);

    if (selectedObject.parent) {
      if (selectedObject.parent.model && selectedObject.parent.getModelMatrix()) {
        mat4.multiply(modelMatrix, selectedObject.parent.getModelMatrix(), modelMatrix);
      }
    }
    gl.uniformMatrix4fv(
      programInfo.uniformLocations.uModelMatrix,
      false,
      modelMatrix,
    );
    gl.uniform4fv(
      programInfo.uniformLocations.uStencilColor,
      vec4.fromValues(0.96, 0.62, 0.09, 1.0),
    );
    gl.bindVertexArray(selectedObject.buffers.vao);

    const offset = 0; // Number of elements to skip before starting
    //if its a mesh then we don't use an index buffer and use drawArrays instead of drawElements
    if (selectedObject.type === ObjectType.MESH) {
      gl.drawArrays(gl.TRIANGLES, offset, selectedObject.buffers.numVertices / 3);
    } else {
      gl.drawElements(
        gl.TRIANGLES,
        selectedObject.buffers.numVertices,
        gl.UNSIGNED_SHORT,
        offset,
      );
    }
    gl.stencilMask(0xff);
    gl.stencilFunc(gl.ALWAYS, 0, 0xff);
  }
}
